import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { ConfigService } from './config.service';
import {
  AboutSectionView,
  AboutSocialLink,
  ApiAboutSection,
  ApiInfo,
} from '../models/info-api.models';
import { ApiConfigService } from './api-config.service';

@Injectable({ providedIn: 'root' })
export class InfoApiService {
  private cachedList$: Observable<ApiInfo[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  /** GET /api-design{suffixe}/info/findall */
  list(forceRefresh = false): Observable<ApiInfo[]> {
    const url = this.apiConfig.infoListUrl;

    if (!this.cachedList$ || forceRefresh) {
      this.cachedList$ = this.http.get<ApiInfo[]>(url).pipe(
        tap((response) => {
          if (this.configService.enableApiLogs) {
            console.log('[InfoApi] list', url, response);
          }
        }),
        shareReplay(1),
      );
    }

    return this.cachedList$;
  }

  getCompanyInfo(forceRefresh = false): Observable<ApiInfo | null> {
    return this.list(forceRefresh).pipe(map((items) => items[0] ?? null));
  }

  toVisibleSections(info: ApiInfo | null): AboutSectionView[] {
    const sections = [...(info?.about ?? [])].sort(
      (a, b) => (a.ordre ?? 0) - (b.ordre ?? 0),
    );

    return sections
      .map((section, index) => this.toSectionView(section, index))
      .filter((section) => section.title || section.content || section.items.length);
  }

  toSocialLinks(info: ApiInfo | null): AboutSocialLink[] {
    if (!info) {
      return [];
    }

    const links: AboutSocialLink[] = [];
    const email = this.trim(info.email);
    const facebook = this.trim(info.facebook_link);
    const linkedin = this.trim(info.linked_in_link);
    const twitter = this.trim(info.twitter_link);
    const whatsapp = this.trim(info.whatsapp_link);

    if (email) {
      links.push({ kind: 'email', href: `mailto:${email}`, label: 'Email' });
    }
    if (facebook) {
      links.push({ kind: 'facebook', href: facebook, label: 'Facebook' });
    }
    if (linkedin) {
      links.push({ kind: 'linkedin', href: linkedin, label: 'LinkedIn' });
    }
    if (twitter) {
      links.push({ kind: 'twitter', href: twitter, label: 'X' });
    }
    if (whatsapp) {
      links.push({ kind: 'whatsapp', href: this.toWhatsappHref(whatsapp), label: 'WhatsApp' });
    }

    return links;
  }

  clearCache(): void {
    this.cachedList$ = null;
  }

  private toSectionView(section: ApiAboutSection, index: number): AboutSectionView {
    return {
      id: section.id || `about-${index}`,
      title: this.trim(section.title),
      content: this.trim(section.content),
      items: (section.items ?? []).map((item) => this.trim(item)).filter(Boolean),
    };
  }

  private toWhatsappHref(value: string): string {
    if (/^https?:\/\//i.test(value) || value.startsWith('whatsapp:')) {
      return value;
    }

    const digits = value.replace(/[^\d]/g, '');
    return digits ? `https://wa.me/${digits}` : value;
  }

  private trim(value?: string | null): string {
    return (value ?? '').trim();
  }
}
